import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useRoles } from '../../context/RolesContext';

function FurtherLearning({ Question, selectedTopic }) {
  const { authFetch } = useAuthFetch();
  const { isAdmin } = useRoles();

  const [youtubeVideoID, setYoutubeVideoID] = useState('');
  const [shortVideoLink, setShortVideoLink] = useState('');
  const [labGuide, setLabGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ratings, setRatings] = useState({ short: { score: 0, userVote: null }, top: { score: 0, userVote: null } });
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [viewerUserId, setViewerUserId] = useState(null);
  const [completions, setCompletions] = useState(new Set());

  useEffect(() => {
    const extractVideoId = (url) => {
      try { return new URL(url).searchParams.get('v'); } catch { return null; }
    };
    setYoutubeVideoID(Question.youtube_link ? extractVideoId(Question.youtube_link) : null);
    setShortVideoLink(Question.short_youtube_video ? extractVideoId(Question.short_youtube_video) : null);
    setLabGuide(Question.lab_guide);
    setRatings({ short: { score: 0, userVote: null }, top: { score: 0, userVote: null } });
    setComments([]);
    setCommentBody('');
    setViewerUserId(null);
    setCompletions(new Set());
    authFetch(`${process.env.REACT_APP_API_URL}/api/video-ratings/${Question.id}`)
      .then(res => res.json())
      .then(data => setRatings(data))
      .catch(() => {});
    authFetch(`${process.env.REACT_APP_API_URL}/api/comments/${Question.id}`)
      .then(r => r.json())
      .then(d => { setComments(d.comments); setViewerUserId(d.viewer_user_id); })
      .catch(() => {});
    authFetch(`${process.env.REACT_APP_API_URL}/api/resource-completions/${Question.id}`)
      .then(r => r.json())
      .then(d => setCompletions(new Set(d.completions)))
      .catch(() => {});
  }, [Question]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVote = (videoType, vote) => {
    const prev = ratings;
    setRatings(r => {
      const currentVote = r[videoType].userVote;
      const delta = vote === 0 ? -(currentVote || 0) : vote - (currentVote || 0);
      return { ...r, [videoType]: { score: r[videoType].score + delta, userVote: vote === 0 ? null : vote } };
    });
    authFetch(`${process.env.REACT_APP_API_URL}/api/video-ratings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ questionId: Question.id, videoType, vote }),
    })
      .then(res => res.json())
      .then(data => setRatings(r => ({ ...r, [videoType]: { score: data.score, userVote: data.vote } })))
      .catch(() => setRatings(prev));
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentBody.trim()) return;
    setCommentLoading(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/api/comments/${Question.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: commentBody.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Failed to post comment.'); return; }
      setComments(prev => [...prev, data]);
      setCommentBody('');
    } finally {
      setCommentLoading(false);
    }
  };

  const deleteComment = async (commentId) => {
    await authFetch(`${process.env.REACT_APP_API_URL}/api/comments/${commentId}`, { method: 'DELETE' });
    setComments(prev => prev.filter(c => c.id !== commentId));
  };

  const requestLabGuide = () => {
    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);
    authFetch(`${process.env.REACT_APP_API_URL}/api/labguides/chatgpt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ topicId: selectedTopic, topicAnswer: Question.correct_answer }),
      signal: controller.signal,
    })
      .then((res) => { clearTimeout(timeoutId); return res.json(); })
      .then((data) => { setLabGuide(data.data); setLoading(false); })
      .catch((err) => {
        clearTimeout(timeoutId);
        setLoading(false);
        if (err.name === 'AbortError') alert('Lab guide request timed out after 120 seconds. Please try again.');
        else { console.error('Error requesting lab guide:', err); alert('Error requesting lab guide.'); }
      });
  };

  const toggleCompletion = async (resourceType) => {
    const isDone = completions.has(resourceType);
    setCompletions(prev => {
      const next = new Set(prev);
      isDone ? next.delete(resourceType) : next.add(resourceType);
      return next;
    });
    try {
      if (isDone) {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/api/resource-completions/${Question.id}/${resourceType}`, { method: 'DELETE' });
        if (!res.ok) throw new Error();
      } else {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/api/resource-completions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question_id: Question.id, resource_type: resourceType }),
        });
        if (!res.ok) throw new Error();
      }
    } catch {
      setCompletions(prev => {
        const next = new Set(prev);
        isDone ? next.add(resourceType) : next.delete(resourceType);
        return next;
      });
    }
  };

  if (!Question) return null;

  return (
    <div className="further-learning">

      {/* Videos */}
      {(shortVideoLink || youtubeVideoID) && (
        <>
          <p className="further-section-title">Watch</p>
          {Question?.youtube_keywords && (
            <p className="further-keywords">Keywords: {Question.youtube_keywords}</p>
          )}
          <div className="further-videos">
            {shortVideoLink && shortVideoLink !== youtubeVideoID && (
              <div className="further-video-block">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <p className="further-video-label">Short · Under 4 min</p>
                  <label className="further-resource-check">
                    <input type="checkbox" checked={completions.has('short_video')} onChange={() => toggleCompletion('short_video')} />
                    Done
                  </label>
                </div>
                <iframe
                  className="further-video-iframe"
                  title="Short YouTube video"
                  src={`https://www.youtube.com/embed/${shortVideoLink}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
                <div className="further-video-rating">
                  <button
                    className={`further-vote-btn${ratings.short.userVote === 1 ? ' active' : ''}`}
                    onClick={() => handleVote('short', ratings.short.userVote === 1 ? 0 : 1)}
                  >👍</button>
                  <span className="further-vote-score">{ratings.short.score > 0 ? `+${ratings.short.score}` : ratings.short.score}</span>
                  <button
                    className={`further-vote-btn${ratings.short.userVote === -1 ? ' active' : ''}`}
                    onClick={() => handleVote('short', ratings.short.userVote === -1 ? 0 : -1)}
                  >👎</button>
                </div>
              </div>
            )}
            {youtubeVideoID && (
              <div className="further-video-block">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <p className="further-video-label">Top Video</p>
                  <label className="further-resource-check">
                    <input type="checkbox" checked={completions.has('video')} onChange={() => toggleCompletion('video')} />
                    Done
                  </label>
                </div>
                <iframe
                  className="further-video-iframe"
                  title="YouTube video"
                  src={`https://www.youtube.com/embed/${youtubeVideoID}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
                <div className="further-video-rating">
                  <button
                    className={`further-vote-btn${ratings.top.userVote === 1 ? ' active' : ''}`}
                    onClick={() => handleVote('top', ratings.top.userVote === 1 ? 0 : 1)}
                  >👍</button>
                  <span className="further-vote-score">{ratings.top.score > 0 ? `+${ratings.top.score}` : ratings.top.score}</span>
                  <button
                    className={`further-vote-btn${ratings.top.userVote === -1 ? ' active' : ''}`}
                    onClick={() => handleVote('top', ratings.top.userVote === -1 ? 0 : -1)}
                  >👎</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Docs */}
      {Question.docs_url && (
        <div className="further-docs">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p className="further-section-title">Docs</p>
            <label className="further-resource-check">
              <input type="checkbox" checked={completions.has('docs')} onChange={() => toggleCompletion('docs')} />
              Done
            </label>
          </div>
          <a className="further-docs-link" href={Question.docs_url} target="_blank" rel="noopener noreferrer">
            Documentation ↗
          </a>
        </div>
      )}

      {/* Lab guide */}
      {Question.lab_guide_viable !== false && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p className="further-section-title">Lab Guide</p>
            {labGuide && (
              <label className="further-resource-check">
                <input type="checkbox" checked={completions.has('lab')} onChange={() => toggleCompletion('lab')} />
                Done
              </label>
            )}
          </div>
          {labGuide ? (
            <div
              className="further-lab-guide"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(labGuide, { FORBID_ATTR: ['style'] }) }}
            />
          ) : loading ? (
            <div className="further-lab-progress">
              <div className="further-lab-progress-fill" />
              <span className="further-lab-progress-label">Generating lab guide…</span>
            </div>
          ) : (
            <button className="further-lab-generate-btn" onClick={requestLabGuide}>
              + Generate Lab Guide (~60s)
            </button>
          )}
        </>
      )}

      {/* Comments */}
      <p className="further-section-title">Comments</p>
      <div className="further-comments">
        {comments.length === 0 && (
          <p className="further-comments-empty">No comments yet. Be the first!</p>
        )}
        {comments.map(c => (
          <div key={c.id} className="further-comment-item">
            <div className="further-comment-meta">
              <span className="further-comment-author">User #{c.user_id}</span>
              <span className="further-comment-date">
                {new Date(c.created_at).toLocaleDateString()}
              </span>
              {(c.user_id === viewerUserId || isAdmin) && (
                <button className="further-comment-delete" onClick={() => deleteComment(c.id)}>Delete</button>
              )}
            </div>
            <p className="further-comment-body">{c.body}</p>
          </div>
        ))}
        <form className="further-comment-form" onSubmit={submitComment}>
          <textarea
            className="further-comment-input"
            placeholder="Add a comment…"
            value={commentBody}
            onChange={e => setCommentBody(e.target.value)}
            maxLength={1000}
            data-form-type="other"
          />
          <button className="further-comment-submit" type="submit" disabled={commentLoading || !commentBody.trim()}>
            {commentLoading ? 'Posting…' : 'Post'}
          </button>
        </form>
      </div>

    </div>
  );
}

export default FurtherLearning;
